import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { setHoveredProgramStep } from '../../store/presentationSlice';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Zoom,
  Fade,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { formatCurrency } from '../../utils/formatters';
import SlideHeader from './SlideHeader';

const ProgramSummarySlide: React.FC<{ isPresenter?: boolean }> = ({ isPresenter = false }) => {
  const dispatch = useDispatch();
  const { calculations, selectedTerms, hoveredProgramStep } = useSelector((state: RootState) => state.presentation);
  
  console.log('ProgramSummarySlide render:', { isPresenter, hoveredProgramStep });
  
  // Constants
  const currentTimeToPayOff = 5.8; // years
  const currentTotalPayments = 86940; // Updated to match the Interest Calculator page
  const proposedTotalPayments = calculations.proposedMonthlyPayment * selectedTerms;

  const getBenefitText = (step: number) => {
    switch (step) {
      case 1:
        return `Save $${Math.round(calculations.currentMonthlyPayment - calculations.proposedMonthlyPayment).toLocaleString()} on your monthly payment`;
      case 2:
        return `Total savings of $${Math.round(currentTotalPayments - proposedTotalPayments).toLocaleString()} over the program term`;
      case 3:
        return "One simple monthly payment";
      case 4:
        return "Professional debt negotiation service";
      default:
        return "";
    }
  };

  return (
    <Box>
      <SlideHeader title="Program Summary" />
      {/* Content Section */}
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
        <Grid container spacing={3}>
          {/* Comparison Section */}
          <Grid item container xs={12} spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
            <Zoom in={true} style={{ transitionDelay: '100ms' }}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ 
                  p: { xs: 2, sm: 3 }, 
                  bgcolor: '#f8f9fa',
                  height: '100%',
                  borderLeft: '4px solid #495057',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  }
                }}>
                  <Typography variant="h6" sx={{ 
                    mb: { xs: 2, sm: 3 }, 
                    textAlign: 'center',
                    color: '#495057',
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '-8px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '40px',
                      height: '3px',
                      bgcolor: '#495057',
                      borderRadius: '2px'
                    }
                  }}>
                    Current Situation
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}>
                      <AccountBalanceWalletIcon sx={{ color: '#495057', fontSize: { xs: '1.75rem', sm: '2rem' } }} />
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">Monthly Payment</Typography>
                        <Typography variant="h5" sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '1.15rem', sm: '1.5rem' }
                        }}>${Math.round(calculations.currentMonthlyPayment).toLocaleString()}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}>
                      <AccessTimeIcon sx={{ color: '#495057', fontSize: { xs: '1.75rem', sm: '2rem' } }} />
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">Time to Pay Off</Typography>
                        <Typography variant="h5" sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '1.15rem', sm: '1.5rem' }
                        }}>{currentTimeToPayOff.toFixed(2)} Years</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}>
                      <TrendingDownIcon sx={{ color: '#495057', fontSize: { xs: '1.75rem', sm: '2rem' } }} />
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">Total Cost</Typography>
                        <Typography variant="h5" sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '1.15rem', sm: '1.5rem' }
                        }}>${currentTotalPayments.toLocaleString()}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Zoom>

            <Zoom in={true} style={{ transitionDelay: '300ms' }}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ 
                  p: { xs: 2, sm: 3 }, 
                  bgcolor: '#ebf7ff',
                  height: '100%',
                  borderLeft: '4px solid #1976d2',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  }
                }}>
                  <Typography variant="h6" sx={{ 
                    mb: { xs: 2, sm: 3 }, 
                    textAlign: 'center',
                    color: '#1976d2',
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '-8px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '40px',
                      height: '3px',
                      bgcolor: '#1976d2',
                      borderRadius: '2px'
                    }
                  }}>
                    Proposed Program
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}>
                      <AccountBalanceWalletIcon sx={{ color: '#1976d2', fontSize: { xs: '1.75rem', sm: '2rem' } }} />
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">Monthly Payment</Typography>
                        <Typography variant="h5" sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '1.15rem', sm: '1.5rem' }
                        }}>${Math.round(calculations.proposedMonthlyPayment).toLocaleString()}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}>
                      <AccessTimeIcon sx={{ color: '#1976d2', fontSize: { xs: '1.75rem', sm: '2rem' } }} />
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">Time to Pay Off</Typography>
                        <Typography variant="h5" sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '1.15rem', sm: '1.5rem' }
                        }}>{(selectedTerms / 12).toFixed(2)} Years</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 1.5, sm: 2 },
                      flexDirection: { xs: 'column', sm: 'row' },
                      textAlign: { xs: 'center', sm: 'left' }
                    }}>
                      <TrendingDownIcon sx={{ color: '#1976d2', fontSize: { xs: '1.75rem', sm: '2rem' } }} />
                      <Box>
                        <Typography variant="subtitle2" color="textSecondary">Total Cost</Typography>
                        <Typography variant="h5" sx={{ 
                          fontWeight: 600,
                          fontSize: { xs: '1.15rem', sm: '1.5rem' }
                        }}>${Math.round(proposedTotalPayments).toLocaleString()}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Zoom>
          </Grid>

          {/* Key Benefits */}
          <Fade in={true} style={{ transitionDelay: '500ms' }}>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Paper elevation={2} sx={{ 
                p: 3,
                background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                borderTop: '4px solid #2e7d32'
              }}>
                <Typography variant="h6" sx={{ 
                  mb: 3,
                  color: '#2e7d32',
                  fontWeight: 600,
                  textAlign: 'center',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '40px',
                    height: '3px',
                    bgcolor: '#2e7d32',
                    borderRadius: '2px'
                  }
                }}>
                  Program Benefits
                </Typography>
                <List sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: { xs: 1.5, sm: 2 }
                }}>
                  {[1, 2, 3, 4].map((step) => (
                    <ListItem 
                      key={step}
                      {...(isPresenter ? {
                        onMouseEnter: () => {
                          console.log('Mouse enter on step:', step);
                          dispatch(setHoveredProgramStep(step));
                        },
                        onMouseLeave: () => {
                          console.log('Mouse leave on step:', step);
                          dispatch(setHoveredProgramStep(null));
                        }
                      } : {})}
                      sx={{ 
                        position: 'relative',
                        bgcolor: '#f1f8f1',
                        borderRadius: 1,
                        mb: 1,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: isPresenter ? 'pointer' : 'default',
                        ...(hoveredProgramStep === step && {
                          bgcolor: '#e8f5e9',
                          transform: 'scale(1.05)',
                          zIndex: 1,
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                        })
                      }}
                    >
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ 
                          color: '#2e7d32',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          ...(hoveredProgramStep === step && {
                            color: '#1b5e20',
                            transform: 'scale(1.2)'
                          })
                        }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={getBenefitText(step)}
                        sx={{
                          '& .MuiTypography-root': {
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            ...(hoveredProgramStep === step && {
                              fontWeight: 600
                            })
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Fade>
        </Grid>
      </Box>
    </Box>
  );
};

export default ProgramSummarySlide;
